if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.submitHandler = this.submitHandler || this.handleSubmit.bind(this);
      this.removeEventListener('submit', this.submitHandler);
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        const variantInput = this.form.querySelector('[name="id"]');
        if (variantInput) variantInput.disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        if (theme.settings.afterAtc !== 'no-js') {
          this.addEventListener('submit', this.submitHandler);
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      // Quick Add reuses this component for different products and signup forms.
      if (evt.target !== this.form || !this.submitBtn) return;

      evt.preventDefault();

      if (this.submitBtn.disabled || this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();
      clearTimeout(this.feedbackTimer);
      const feedback = this.querySelector('[data-cart-add-status]');
      if (feedback) feedback.textContent = '';

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      // Analytics must never gate a purchase, nor turn a successful add into an
      // apparent failure. Read the previous cart in parallel, best-effort only.
      const oldCartPromise = ProductForm.readCart();
      let added = false;
      try {
        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        if (!response.ok || data.status) {
          const message = typeof data.description === 'string' ? data.description : data.message;
          throw new Error(message || theme.strings.cartError);
        }
        added = true;
        this.submitBtn.classList.add('is-success');
        if (feedback) feedback.textContent = this.submitBtn.dataset?.cartAddedMessage || 'Adăugat în coș';

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          // Await rendering so a failed section/script load has a safe fallback
          // instead of an unhandled rejection and an apparently empty cart.
          if (this.cartDrawer) {
            await this.cartDrawer.renderContents(
              data,
              !quickAddDrawer && theme.settings.afterAtc === 'drawer'
            );
          } else if (window.location.pathname === theme.routes.cart) {
            const cartItems = document.querySelector('cart-items');
            if (cartItems) {
              if (cartItems.dataset.empty === 'true') {
                window.location.reload();
              } else {
                await cartItems.refreshCart();
              }
            }
          }
        }
        this.notifyCartChange(data, oldCartPromise);
      } catch (error) {
        if (added) {
          // The server already accepted the item. Never invite a retry (which
          // would add it twice); recover through Shopify's real cart page.
          window.location.href = theme.routes.cart;
          return;
        }
        const message = error.name !== 'SyntaxError' && error.name !== 'TypeError' && error.message && !/^(Failed to fetch|Load failed|NetworkError|\d+)$/.test(error.message)
          ? error.message : theme.strings.cartError;
        this.setErrorMsgState(message);
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: message
          }
        }));
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.toggle('is-success', added);
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
        this.feedbackTimer = setTimeout(() => { if (feedback) feedback.textContent = ''; }, 2000);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && response.sections?.['cart-icon-bubble']) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    static async readCart() {
      try {
        const response = await fetch(`${theme.routes.cart}.js`);
        return response.ok ? await response.json() : null;
      } catch { return null; }
    }

    async notifyCartChange(data, oldCartPromise) {
      try {
        const [oldCart, cart] = await Promise.all([oldCartPromise, ProductForm.readCart()]);
        if (!cart) return;
        const oldItem = oldCart?.items?.find((item) => data.key ? item.key === data.key : item.variant_id === data.variant_id);
        const newItem = cart.items?.find((item) => data.key ? item.key === data.key : item.variant_id === data.variant_id);
        this.dispatchEvent(new CustomEvent(oldItem ? 'on:line-item:change' : 'on:cart:add', {
          bubbles: true,
          detail: { cart, variantId: data.variant_id, ...(oldItem ? { oldQuantity: oldItem.quantity, newQuantity: newItem?.quantity ?? data.quantity } : {}) }
        }));
      } catch {}
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) this.errorMsg.textContent = error;
    }
  }

  customElements.define('product-form', ProductForm);
}
